import { LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, Tab, Tabs, ThemeProvider } from "@mui/material";
import { BaseLink } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import qs from "query-string";
import { useHistory, useLocation } from "react-router-dom";

import { PageWrapper } from "../../redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "../../redesign/components/StickyScrollPageHeader";
import { PageContent } from "../../redesign/WorkerShifts/components/PageContent";
import { useReferrals } from "./api/useReferrals";
import { WorkplaceReferralsListCard } from "./components/WorkplaceReferralsListCard/WorkplaceReferralsListCard";
import { WORKPLACE_AFFILIATE_STATS_SCREEN_NAME } from "./constants";
import { WORKPLACE_REFERRALS_WITH_ACCOUNTS_PATH, WorkplaceReferralRouterPath } from "./paths";
import {
  RefereeType,
  ReferralConversionStage,
  WorkplaceReferralAction,
  WorkplaceReferralComponent,
} from "./types";

const WORKPLACE_REFERRAL_TABS = [
  { label: "Successful", id: ReferralConversionStage.CONVERTED },
  { label: "Pending", id: ReferralConversionStage.UNCONVERTED },
];
export function WorkplaceReferralsStatsPage() {
  const worker = useDefinedWorker();

  const history = useHistory();
  const location = useLocation();
  const { search } = location;

  useLogEffect(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL_STATS, {
    action: WorkplaceReferralAction.PAGE_VIEWED,
    component: WorkplaceReferralComponent.WORKPLACE_REFERRAL_STATS_PAGE,
    pageQueryParams: qs.parse(search),
  });

  const selectedTab =
    WORKPLACE_REFERRAL_TABS.find(({ id }) => id === qs.parse(search)?.selectedTab) ??
    WORKPLACE_REFERRAL_TABS[0];

  const {
    data: referralStats,
    isLoading: isLoadingReferralStats,
    isSuccess: isReferralStatsSuccess,
  } = useReferrals({
    type: RefereeType.WORKPLACE,
    referrerId: worker.userId,
    conversion: selectedTab.id,
  });

  const handleClickBack = () => {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL_STATS, {
      action: WorkplaceReferralAction.BACK_BUTTON_CLICKED,
      component: WorkplaceReferralComponent.WORKPLACE_REFERRAL_STATS_PAGE,
    });
    if (isDefined(location.key)) {
      history.goBack();
    } else {
      history.push(WORKPLACE_REFERRALS_WITH_ACCOUNTS_PATH);
    }
  };

  const hasReferrals = isDefined(referralStats?.data) && referralStats?.data.length > 0;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper>
        <StickyScrollPageHeader
          title={WORKPLACE_AFFILIATE_STATS_SCREEN_NAME}
          headerVariant="tertiary"
          onClickBack={handleClickBack}
        />
        <Box
          sx={(theme) => ({
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: theme.palette.common.white,
          })}
        >
          <Tabs
            value={selectedTab.id}
            aria-label="Select referral conversion type tabs"
            variant="fullWidth"
            sx={{
              width: "100%",
              border: "none",
              borderRadius: 0,
              borderBottom: (theme) => `3px solid ${theme.palette.background.secondary}`,
              "& .MuiTabs-indicator": {
                backgroundColor: (theme) => theme.palette.text.primary,
                height: 3,
                bottom: 0,
                top: "auto",
              },
              "& .MuiTab-root": {
                minHeight: 45,
                color: (theme) => theme.palette.text.disabled,
                backgroundColor: "transparent",
              },
              "& .MuiTab-root.Mui-selected": {
                color: (theme) => theme.palette.text.primary,
              },
            }}
            onChange={() => {
              logEvent(APP_V2_USER_EVENTS.WORKPLACE_REFERRAL, {
                action: WorkplaceReferralAction.REFERRAL_STATS_TAB_CLICKED,
                component: WorkplaceReferralComponent.WORKPLACE_REFERRAL_STATS_TAB_ITEM,
              });
            }}
          >
            {WORKPLACE_REFERRAL_TABS.map(({ label, id }) => (
              <Tab
                key={id}
                replace
                value={id}
                label={label}
                id={`simple-tab-${id}`}
                aria-controls={`simple-tabpanel-${id}`}
                component={BaseLink}
                to={{
                  pathname: WorkplaceReferralRouterPath.WORKPLACE_REFERRALS_STATS_PATH,
                  search: `?selectedTab=${id}`,
                }}
              />
            ))}
          </Tabs>
        </Box>
        <PageContent>
          <Stack marginTop={4} spacing={4}>
            {isLoadingReferralStats && (
              <Stack
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <LoadingSpinner size="large" />
              </Stack>
            )}
            {isReferralStatsSuccess && !hasReferrals && (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 200,
                }}
              >
                <Text variant="body1" textAlign="center">
                  No referrals
                </Text>
              </Stack>
            )}
            {isReferralStatsSuccess &&
              hasReferrals &&
              referralStats.data.map(({ workplace, refereeBonus }) => (
                <WorkplaceReferralsListCard
                  key={workplace.id}
                  workplaceName={workplace.name}
                  workplaceUserEmail={workplace.user.email}
                  workplaceUserName={workplace.user.name}
                  {...(selectedTab.id === ReferralConversionStage.CONVERTED
                    ? { bonusAmount: refereeBonus?.amountApplied }
                    : {})}
                />
              ))}
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
